'use client';

import { DisplayPrice } from '@/shared/components/display-price';
import { Link } from '@/shared/components/primitives/link';
import { useTranslations } from 'next-intl';
import { PriceIncludingWildMembership, PromotionData } from '@/shared/types/prices';
import { getPriceByType } from '@/features/games/shared/helpers/get-price-by-type';
import { formatPromotionDate } from '@/features/games/internal/helpers/format-promotion-date';

type Props = {
  prices: PriceIncludingWildMembership[];
  isMember: boolean;
  promotion?: PromotionData;
};

export const PurchaseDescription = ({ prices, isMember, promotion }: Props) => {
  const t = useTranslations('GamePage.purchaseGame');

  const currentPrice = getPriceByType(prices, 'current');
  const memberPrice = getPriceByType(prices, 'member');
  const salePrice = getPriceByType(prices, 'sale');
  const standardPrice = getPriceByType(prices, 'price');
  if (!currentPrice) return null;

  const currentPromotionalPrice = getPriceByType(promotion?.promotionalPrices || [], 'current');
  const lineThroughPrice = isMember && standardPrice ? standardPrice.value : currentPrice.value;
  const currency = currentPrice.currencyCode;

  if (currentPromotionalPrice && promotion) {
    const formattedEndDate = formatPromotionDate(promotion.endDate);

    return (
      <div className='flex flex-col text-sm text-white'>
        <div>
          <DisplayPrice
            value={currentPromotionalPrice?.value}
            currencyCode={currency}
            className='text-primary text-xl font-bold'
          />
          <DisplayPrice
            value={lineThroughPrice}
            currencyCode={currency}
            className='pl-2 text-gray-400 line-through'
          />
        </div>
        <span className='ml-1 text-xs text-orange-300'>
          {t('promotionDiscount', {
            percentage: promotion.percentage,
            endDate: formattedEndDate,
          })}
        </span>
      </div>
    );
  }

  if (salePrice && standardPrice) {
    return (
      <div className='text-sm text-white'>
        <DisplayPrice
          value={salePrice.value}
          currencyCode={currency}
          className='text-primary text-xl font-bold'
        />
        <span className='ml-2 text-orange-300'>
          {t.rich('savings', {
            original: () => (
              <span className='ml-2 line-through'>
                <DisplayPrice value={salePrice.previousValue!} currencyCode={currency} />
              </span>
            ),
            savings: () => (
              <DisplayPrice
                value={salePrice.previousValue! - salePrice.value}
                currencyCode={currency}
              />
            ),
          })}
        </span>
      </div>
    );
  }
  if (isMember && memberPrice && standardPrice && memberPrice.value !== standardPrice.value) {
    return (
      <div className='text-sm text-white'>
        <DisplayPrice
          value={currentPrice.value}
          currencyCode={currency}
          className='text-primary text-xl font-bold'
        />
        <span className='ml-2 text-orange-300'>
          {t.rich('savingsMember', {
            original: () => (
              <span className='line-through'>
                <DisplayPrice value={standardPrice.value} currencyCode={currency} />
              </span>
            ),
            savings: () => (
              <DisplayPrice
                value={standardPrice.value - memberPrice.value}
                currencyCode={currency}
              />
            ),
          })}
        </span>
      </div>
    );
  }

  if (!isMember && memberPrice && memberPrice.value !== currentPrice.value) {
    return (
      <div className='text-sm text-white'>
        <DisplayPrice
          value={currentPrice.value}
          currencyCode={currency}
          className='text-primary text-xl font-bold'
        />
        <span className='ml-2 text-orange-300'>
          (
          {t.rich('memberPrice', {
            price: () => <DisplayPrice value={memberPrice.value} currencyCode={currency} />,
          })}
          <Link href='/join-wild' className='ml-1 underline'>
            {t('joinWild')})
          </Link>
        </span>
      </div>
    );
  }

  return (
    <DisplayPrice
      value={currentPrice.value}
      currencyCode={currency}
      className='text-primary text-xl font-bold'
    />
  );
};
